import { computed, onMounted, onUnmounted, ref, watch } from "vue";

export const errorMessage = error =>
  (error.body && error.body.message) ||
  error.message ||
  "Unable to load the list. Please try again.";

export const useAdminList = (fetchList, defaultSort) => {
  const status = ref("active");
  const search = ref("");
  const appliedSearch = ref("");
  const sort = ref(defaultSort);
  const page = ref(1);
  const data = ref([]);
  const total = ref(0);
  const totalPages = ref(0);
  const counts = ref({ active: null, stale: null, inactive: null });
  const reviewWindow = ref(null);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref("");
  let revision = 0;
  let debounce;
  let disposed = false;
  const context = computed(
    () =>
      `${status.value}\n${search.value}\n${JSON.stringify(reviewWindow.value)}`
  );
  const refresh = async () => {
    const request = ++revision;
    loading.value = true;
    error.value = "";
    try {
      const response = await fetchList({
        status: status.value,
        search: appliedSearch.value,
        sortBy: sort.value,
        page: page.value,
        limit: 100
      });
      if (request !== revision || disposed) return;
      if (!response || !Array.isArray(response.results))
        throw new Error("Unable to load the list. Please try again.");
      counts.value = response.counts;
      reviewWindow.value = response.teamReviewWindow || null;
      total.value = response.totalResults;
      totalPages.value = response.totalPages;
      if (page.value > Math.max(1, totalPages.value)) {
        page.value = Math.max(1, totalPages.value);
        return refresh();
      }
      data.value = response.results;
      loaded.value = true;
      if (status.value === "stale" && !reviewWindow.value)
        status.value = "active";
    } catch (err) {
      if (request !== revision || disposed) return;
      error.value = errorMessage(err);
      data.value = [];
      if (err.status === 503 || (err.response && err.response.status === 503)) {
        reviewWindow.value = null;
        counts.value.stale = null;
      }
    } finally {
      if (request === revision && !disposed) loading.value = false;
    }
  };
  watch(search, () => {
    clearTimeout(debounce);
    revision += 1;
    data.value = [];
    loading.value = true;
    debounce = setTimeout(() => {
      appliedSearch.value = search.value.trim();
      page.value = 1;
      refresh();
    }, 300);
  });
  watch(status, () => {
    clearTimeout(debounce);
    appliedSearch.value = search.value.trim();
    page.value = 1;
    data.value = [];
    refresh();
  });
  const changePage = next => {
    if (loading.value) return;
    page.value = next;
    refresh();
  };
  const changeSort = ({ key, direction }) => {
    sort.value = `${key}:${direction}`;
    page.value = 1;
    refresh();
  };
  onMounted(refresh);
  onUnmounted(() => {
    disposed = true;
    revision += 1;
    clearTimeout(debounce);
  });
  return {
    status,
    search,
    appliedSearch,
    sort,
    page,
    data,
    total,
    totalPages,
    counts,
    reviewWindow,
    loading,
    loaded,
    error,
    context,
    refresh,
    changePage,
    changeSort
  };
};
